import { Injectable, signal } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { ApiService } from './ApiService';
import { AuthenticationService } from './authentication.service';
import { Urls } from './urls';

export type BusinessType = 'restaurant' | 'store' | 'other';

/**
 * Active company's `business_type`, fetched once per company context (a superadmin can
 * switch companies, so the cache is keyed by `effectiveCompanyId`).
 */
@Injectable({ providedIn: 'root' })
export class CompanyBusinessTypeService {
  readonly businessType = signal<BusinessType | null>(null);

  private loadedFor: number | null | undefined = undefined;
  private request$: Observable<BusinessType | null> | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthenticationService,
  ) { }

  load(force = false): Observable<BusinessType | null> {
    const companyId = this.authService.effectiveCompanyId;
    if (!force && this.request$ && companyId === this.loadedFor) {
      return this.request$;
    }

    this.loadedFor = companyId;
    this.businessType.set(null);

    if (companyId === null) {
      this.request$ = of(null);
      return this.request$;
    }

    this.request$ = this.apiService.getPetition(Urls.urls.company).pipe(
      map((result: any) => result?.list?.[0]?.business_type ?? null),
      catchError(() => of(null)),
      tap(type => this.businessType.set(type)),
      shareReplay(1),
    );
    this.request$.subscribe();
    return this.request$;
  }
}
